/* eslint-disable @typescript-eslint/explicit-member-accessibility -- b*/
/* eslint-disable no-console -- depends on console */
import type { Film } from '@vista-digital/ocapi-types/v1'
import { getMemberHash, loadMember } from '@vista-digital/occ-domain'
import { getLogger } from '../services'
import { getFilmHoCodeFromFilmId } from '../utils/getFilmHoCodeFromFilmId'
import { isDebug } from '../utils/isDebug'
import { isSignedIn } from '../utils/isSignedIn'

declare const mt: (method: string, eventName: string, eventData?: object, callback?: (success: boolean) => void) => void

export class MovioTracker {
    get logger() {
        return getLogger('MovioTracker').value
    }

    async setMember(): Promise<boolean> {
        // Determine if member is signed in and details available

        if (isSignedIn()) {
            await loadMember()
            const memberHash = getMemberHash()

            const memberDetails = {
                id: memberHash,
                hashed: true,
            }

            if (memberHash) {
                mt('set', 'memberId', memberDetails)
                return true
            }
        }

        return false
    }

    checkMT(): boolean {
        // Check if MovieTracker object actually exists
        if (!mt) {
            this.logger.error('MT did not load')
            return false
        }

        return true
    }

    async validateMT(): Promise<boolean> {
        const setMemberResult = await this.setMember()

        if (!(this.checkMT() && setMemberResult)) {
            this.logger.error('MT memberResult returned false')
            return false
        }

        return true
    }

    async initiateLogin(): Promise<void> {
        // Checks if a user has just logged in
        if (isSignedIn()) {
            await this.memberLogin()
        }
    }

    async memberLogin(): Promise<void> {
        // A user logged in the website

        const valid = await this.validateMT()

        if (!valid) {
            return
        }

        mt('send', 'TrackMemberLogin', { loginDateTime: new Date() }, function (success) {
            if (isDebug()) {
                console.log('Sent TrackMemberLogin event: ', success)
            }
        })
    }

    async movieDetail(filmId: string, waitMS: number): Promise<void> {
        // A user visited a movie detail page for a certain amount of time
        const valid = await this.validateMT()
        const filmHoCodeString = getFilmHoCodeFromFilmId(filmId)

        if (!valid) {
            return
        }

        setTimeout(() => {
            mt(
                'send',
                'TrackMovieDetail',
                {
                    movieId: filmHoCodeString,
                    viewDuration: waitMS / 1000,
                    viewDateTime: new Date(),
                },
                function (success) {
                    if (isDebug()) {
                        console.log('Sent TrackMovieDetail event: ', success)
                        console.log('Tracked film details page viewing for: ' + filmHoCodeString)
                    }
                }
            )
        }, waitMS)
    }

    /**
     * Only to be called once a trailer has been watched for more than 20 seconds.
     */
    async movieTrailer(film: Film): Promise<void> {
        const valid = await this.validateMT()
        const filmHoCode = film.hoCode

        if (!valid) {
            this.logger.warn('Unable to track trailer watched as movio tracking is invalid.')
            return
        }

        mt('send', 'TrackMovieTrailer', { movieId: filmHoCode, viewDateTime: new Date() }, function (success: boolean) {
            if (success) {
                console.info('Completed tracking for trailer watched', { filmHoCode })
            } else {
                console.warn('Failed to track trailer watched', { filmHoCode })
            }
        })
    }

    async movieWatchList(filmId: string, isDelete: boolean): Promise<void> {
        // A user added/removed a movie to his/her watch list

        const valid = await this.validateMT()
        const filmHoCodeString = getFilmHoCodeFromFilmId(filmId)

        if (!valid) {
            return
        }

        if (!isDelete) {
            mt('send', 'TrackMovieWatchlist', { movieId: filmHoCodeString }, function (success) {
                if (isDebug()) {
                    console.log('Sent TrackAddToWatchlist event: ', success)
                    console.log('Added film to watchlist: ' + filmHoCodeString)
                }
            })
        } else {
            mt('delete', 'TrackMovieWatchlist', { movieId: filmHoCodeString }, function (success) {
                if (isDebug()) {
                    console.log('Sent TrackRemoveFromWatchlist event: ', success)
                    console.log('Removed film from watchlist: ' + filmHoCodeString)
                }
            })
        }
    }

    async abandonOrder(filmId: string): Promise<void> {
        // A user abandoned an order in the booking flow

        const valid = await this.validateMT()
        const filmHoCodeString = getFilmHoCodeFromFilmId(filmId)

        if (!valid) {
            return
        }

        mt(
            'send',
            'TrackAbortedPurchase',
            {
                movieId: filmHoCodeString,
                abortDateTime: new Date(),
            },
            function (success) {
                if (isDebug()) {
                    console.log('Sent AbandonOrder event: ', success)
                    console.log('Abandoned ticket purchase for film id: ' + filmHoCodeString)
                }
            }
        )
    }
}
